<template>
  <div class="question-card bg-white rounded-2xl shadow-2xl p-6 md:p-8 max-w-4xl mx-auto">
    <!-- Question Header -->
    <div class="question-header mb-6">
      <h3 class="question-text text-xl md:text-2xl font-bold text-gray-800 leading-relaxed">
        {{ question.text }}
      </h3>
    </div>

    <!-- Optional Question Image -->
    <div v-if="question.image_url" class="question-image-container mb-6 relative">
      <img
        v-if="!imageError"
        :src="question.image_url"
        alt="Question illustration"
        class="question-image w-full rounded-lg shadow-md max-h-64 object-cover"
        @load="imageLoaded = true"
        @error="handleImageError"
      />
      <div v-if="!imageLoaded && !imageError" class="image-loader flex items-center justify-center h-64 bg-gray-100 rounded-lg">
        <div class="spinner border-4 border-purple-200 border-t-purple-600 rounded-full w-12 h-12 animate-spin"></div>
      </div>
    </div>

    <!-- Answer Options -->
    <div class="options-grid grid grid-cols-1 sm:grid-cols-2 gap-4">
      <button
        v-for="option in displayOptions"
        :key="option.id"
        class="option-button relative flex items-center justify-between p-4 rounded-xl border-2 transition-all duration-300 min-h-[60px] text-left"
        :class="getOptionClass(option)"
        :disabled="disabled || selectedOptionId !== null"
        @click="selectOption(option.id)"
        :aria-label="`Option: ${option.text}`"
      >
        <span class="option-text flex-1 font-medium text-base md:text-lg">{{ option.text }}</span>
        <i
          v-if="getOptionIcon(option)"
          class="fas option-icon text-2xl ml-3"
          :class="getOptionIcon(option)"
        ></i>
      </button>
    </div>

    <!-- Feedback Message -->
    <transition name="fade">
      <div
        v-if="feedback"
        class="feedback-message mt-6 p-4 rounded-lg flex items-center gap-3 text-lg font-semibold"
        :class="feedback.isCorrect ? 'bg-green-50 text-green-800 border-2 border-green-300' : 'bg-red-50 text-red-800 border-2 border-red-300'"
        role="alert"
        :aria-live="'polite'"
      >
        <i class="fas text-2xl" :class="feedback.isCorrect ? 'fa-check-circle' : 'fa-times-circle'"></i>
        <span>{{ feedback.isCorrect ? 'Correct! Well done!' : 'Not quite right, but keep learning!' }}</span>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'QuestionCard',
  props: {
    question: {
      type: Object,
      required: true
      // Structure: { id, text, options: [{ id, text }], order, image_url, correct_option_id }
    },
    feedback: {
      type: Object,
      default: null
      // Structure: { isCorrect: boolean, selectedOptionId: number, correctOptionId: number }
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ['answer-selected'],
  data() {
    return {
      selectedOptionId: null,
      imageLoaded: false,
      imageError: false,
      shuffledOptions: []
    }
  },
  computed: {
    /**
     * Get shuffled options for current question
     * Shuffles once per question to avoid re-shuffling on re-renders
     */
    displayOptions() {
      return this.shuffledOptions.length > 0 ? this.shuffledOptions : this.question.options
    }
  },
  methods: {
    /**
     * Shuffle options using Fisher-Yates algorithm
     * Called once per question to randomize answer order
     */
    shuffleOptions() {
      const options = [...this.question.options]
      for (let i = options.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [options[i], options[j]] = [options[j], options[i]]
      }
      this.shuffledOptions = options
    },

    /**
     * Handle option selection
     * @param {number} optionId - Selected option ID
     */
    selectOption(optionId) {
      if (this.disabled || this.selectedOptionId) return

      this.selectedOptionId = optionId
      this.$emit('answer-selected', optionId)
    },

    /**
     * Get CSS class for option based on feedback state
     * @param {Object} option - Option object
     * @returns {string} CSS classes
     */
    getOptionClass(option) {
      // No feedback yet - default state
      if (!this.feedback) {
        return 'option-default border-gray-300 bg-white hover:bg-purple-50 hover:border-purple-400 cursor-pointer'
      }

      // User selected this option
      if (this.feedback.selectedOptionId === option.id) {
        if (this.feedback.isCorrect) {
          return 'option-correct bg-green-100 border-green-500 text-green-900'
        } else {
          return 'option-incorrect bg-red-100 border-red-500 text-red-900'
        }
      }

      // Show correct answer if user was wrong
      if (!this.feedback.isCorrect && this.feedback.correctOptionId === option.id) {
        return 'option-correct-reveal bg-green-50 border-green-400 text-green-800'
      }

      // Other options - dimmed
      return 'option-dimmed border-gray-200 bg-gray-50 text-gray-500 cursor-not-allowed opacity-60'
    },

    /**
     * Get icon class for option based on feedback
     * @param {Object} option - Option object
     * @returns {string|null} Icon class or null
     */
    getOptionIcon(option) {
      if (!this.feedback) return null

      // Checkmark for correct answer
      if (this.feedback.selectedOptionId === option.id && this.feedback.isCorrect) {
        return 'fa-check-circle text-green-600'
      }

      // X for incorrect selection
      if (this.feedback.selectedOptionId === option.id && !this.feedback.isCorrect) {
        return 'fa-times-circle text-red-600'
      }

      // Checkmark for correct answer reveal
      if (!this.feedback.isCorrect && this.feedback.correctOptionId === option.id) {
        return 'fa-check-circle text-green-600'
      }

      return null
    },

    /**
     * Handle image loading error
     */
    handleImageError() {
      this.imageError = true
      this.imageLoaded = false
    }
  },
  mounted() {
    // Shuffle options on initial load
    this.shuffleOptions()
  },
  watch: {
    // Reset selection and shuffle options when question changes
    question() {
      this.selectedOptionId = null
      this.imageLoaded = false
      this.imageError = false
      this.shuffleOptions()
    }
  }
}
</script>

<style scoped>
/* Fade transition for feedback message */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* Option button animations */
.option-button {
  transform: scale(1);
}

.option-button:not(:disabled):hover {
  transform: scale(1.02);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
}

.option-button:active:not(:disabled) {
  transform: scale(0.98);
}

/* Loading spinner animation */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.spinner {
  animation: spin 1s linear infinite;
}

/* Ensure touch targets are at least 44px */
@media (max-width: 768px) {
  .option-button {
    min-height: 64px;
  }
}
</style>
